/**
 * The "one native Google sign-in at a time" guard, shared between
 * login-form.tsx (starts an attempt) and native-auth-bridge.tsx (ends one,
 * either by redeeming the deep link or by observing the Custom Tab close
 * without one). The two live in unrelated parts of the component tree —
 * one page-scoped, one mounted ONCE at the root layout and outliving every
 * individual attempt — so a plain module-level flag stands in for shared
 * state. Nothing here needs to trigger a render: it's read and written
 * imperatively around each attempt.
 *
 * Both fields describe the CURRENT ATTEMPT, not this module's lifetime, and
 * are reset together at every attempt boundary: `startNativeSignIn` at the
 * start, `cancelNativeSignIn` at the end of any attempt that didn't produce
 * a session. Because native-auth-bridge.tsx's listeners are set up once and
 * live across attempts, a field that isn't re-armed at the start of a new
 * attempt would carry a stale value from the previous one — e.g. a
 * `deepLinkHandled` left `true` from an earlier malformed deep link would
 * wrongly suppress the NEXT attempt's genuine-cancellation handling. Keeping
 * every field's reset paired with these two functions, rather than left to
 * call sites, is what keeps that invariant from drifting.
 */

let nativeSignInInFlight = false;
let deepLinkHandled = false;

export function isNativeSignInInFlight(): boolean {
  return nativeSignInInFlight;
}

export function startNativeSignIn(): void {
  nativeSignInInFlight = true;
  deepLinkHandled = false;
}

/**
 * Marks that the `/native/auth` return link has been recognized for the
 * current attempt.
 * Call this before `Browser.close()` runs in native-auth-bridge.tsx — on
 * Android that call alone can trigger `browserFinished`, and this is what
 * tells that self-triggered event apart from a genuine cancellation of some
 * OTHER attempt.
 */
export function markDeepLinkHandled(): void {
  deepLinkHandled = true;
}

export function wasDeepLinkHandled(): boolean {
  return deepLinkHandled;
}

/**
 * Re-arms the guard after an attempt that did NOT end in a redeemed
 * session — the user backed out of the Custom Tab, or the deep link that
 * brought the app back didn't carry a usable code. Do not call this on the
 * success path: the deep link already navigates the app away, so re-arming
 * there is pointless and risks racing the in-flight redemption in
 * native-auth-bridge.tsx.
 */
export function cancelNativeSignIn(): void {
  nativeSignInInFlight = false;
  deepLinkHandled = false;
}
